// Top-level at-rule handler for import resolution and ordering diagnostics.
// It preserves css imports and inlines `.luis` imports with recursion callback.
import { isCharsetName, parseImportRequest } from './classify'
import { parseTopLevelAtRuleName } from './chars'
import { markContentSeen, trackCharsetDirective, trackImportDirective, trackLayerDirective, type DirectiveOrderState } from './order'
import { normalizeResolvedImport } from './resolve-utils'
import { buildCssPassthroughImport, resolveCssImportHref } from './handler-css'
import { isUnsafeCssImportStatement, isUnsafeImportRequest } from './handler-security'
import { pushImportWarning } from './handler-warnings'
import type { ImportResolveContext, ResolveNestedSource } from './types'
import { appendWrappedImportSource, createFlatResolvedImportSource, createResolvedImportSource } from './line-map'
import type { ResolvedImportSource } from './line-map-types'

// Build one rendered output chunk from a top-level at-rule declaration.
export function processTopLevelAtRule(
  statement: string,
  line: number,
  sourceId: string,
  context: ImportResolveContext,
  orderState: DirectiveOrderState,
  resolveNested: ResolveNestedSource,
): ResolvedImportSource {
  const name = parseTopLevelAtRuleName(statement)

  // Check whether the current at-rule is a charset alias.
  if (isCharsetName(name)) {
    trackCharsetDirective(orderState, line, sourceId, context.warnings)
    // Return the original charset statement unchanged.
    return createFlatResolvedImportSource(statement, sourceId)
  } else if (name === 'layer') {
    // Validate layer declaration ordering.
    trackLayerDirective(orderState, line, sourceId, context.warnings)
    // Return the original layer statement unchanged.
    return createFlatResolvedImportSource(statement, sourceId)
  } else if (name !== 'import') {
    // Keep unrelated declaration at-rules unchanged.
    markContentSeen(orderState)
    // Return unrelated top-level at-rules unchanged.
    return createFlatResolvedImportSource(statement, sourceId)
  } else {
    // Continue import-specific resolution flow.
  }

  const request = parseImportRequest(statement)
  // Keep malformed import statements untouched.
  if (!request) {
    // Drop malformed imports when shared guard policy still detects unsafe payload.
    if (isUnsafeCssImportStatement(statement)) {
      pushImportWarning(context, 'invalid-import-url', line, `[${sourceId}] Unsafe @import URL blocked.`)
      return createResolvedImportSource()
    } else {
      // Continue preserving malformed imports without unsafe resource payloads.
    }
    markContentSeen(orderState)
    // Return malformed import statements unchanged.
    return createFlatResolvedImportSource(statement, sourceId)
  } else {
    // Continue import-order validation and resolution.
  }
  // Block unsafe import protocols like `javascript:` early.
  if (isUnsafeImportRequest(request.raw)) {
    pushImportWarning(context, 'invalid-import-url', line, `[${sourceId}] Unsafe @import URL blocked: ${request.raw}`)
    // Return an empty string for blocked unsafe imports.
    return createResolvedImportSource()
  } else {
    // Continue with safe import request handling.
  }

  const phaseBeforeImport = orderState.phase
  trackImportDirective(orderState, request, line, sourceId, context.warnings)
  // Block CSS-pass imports that would load remote or executable resources.
  if (request.kind !== 'luis' && isUnsafeCssImportStatement(statement)) {
    pushImportWarning(context, 'invalid-import-url', line, `[${sourceId}] Unsafe CSS @import blocked: ${request.raw}`)
    return createResolvedImportSource()
  } else {
    // Continue `.luis` imports and safe local CSS imports.
  }
  // Rewrite css imports with source-relative absolute href for style-tag runtime.
  if (request.kind === 'css') {
    const resolvedCssHref = resolveCssImportHref(request.raw, sourceId)
    // Keep original statement when href cannot be resolved safely.
    if (!resolvedCssHref) {
      // Return the original CSS import when href resolution fails.
      return createFlatResolvedImportSource(statement, sourceId)
    } else {
      // Return normalized css import statement with resolved href.
      return createFlatResolvedImportSource(buildCssPassthroughImport(statement, resolvedCssHref), sourceId)
    }
  } else if (request.kind !== 'luis') {
    // Keep unknown import kinds untouched in final CSS output.
    // Return unknown import kinds unchanged.
    return createFlatResolvedImportSource(statement, sourceId)
  } else {
    // Continue `.luis` import inlining.
  }

  // Warn and drop `.luis` import when resolver is not configured.
  if (!context.options.importResolver) {
    pushImportWarning(
      context,
      'unsupported-import',
      line,
      `[${sourceId}] Runtime/dynamic .luis import is disabled. Use compile-time importResolver.`,
    )
    // Return an empty string when `.luis` imports cannot be resolved.
    return createResolvedImportSource()
  } else {
    // Continue compile-time resolver lookup.
  }

  const resolved = context.options.importResolver(request.raw, sourceId)
  // Warn and drop unresolved `.luis` imports.
  if (!resolved) {
    pushImportWarning(context, 'import-not-found', line, `[${sourceId}] Unable to resolve .luis import: ${request.raw}`)
    // Return an empty string for unresolved `.luis` imports.
    return createResolvedImportSource()
  } else {
    // Continue cycle checks for resolved import target.
  }

  const normalized = normalizeResolvedImport(request.raw, resolved)
  const importMode = context.options.importModeResolver?.(normalized.id)
  // Keep vendor-fast imports as css-pass when directive ordering still allows @import.
  if (importMode === 'vendor-fast' && phaseBeforeImport <= 3) {
    // Return a passthrough CSS import for vendor-fast sources.
    return createFlatResolvedImportSource(buildCssPassthroughImport(statement, normalized.id), sourceId)
  } else {
    // Continue with regular inline import flow.
  }
  // Detect cyclic imports and skip recursive expansion.
  if (context.importStack.includes(normalized.id)) {
    pushImportWarning(context, 'import-cycle', line, `[${sourceId}] Import cycle detected for "${normalized.id}".`)
    // Return an empty string when an import cycle is detected.
    return createResolvedImportSource()
  } else {
    // Continue nested import resolution for acyclic graph.
  }

  const nextDepth = context.importStack.length + 1
  // Drop nested import when recursion depth exceeds configured budget.
  if (nextDepth > context.resourceLimits.maxImportDepth) {
    pushImportWarning(
      context,
      'resource-limit',
      line,
      `[${sourceId}] Import depth budget exceeded (${nextDepth}/${context.resourceLimits.maxImportDepth}) for "${normalized.id}".`,
    )
    return createResolvedImportSource()
  } else if (context.importCount + 1 > context.resourceLimits.maxImportCount) {
    // Drop nested import when total import count exceeds configured budget.
    pushImportWarning(
      context,
      'resource-limit',
      line,
      `[${sourceId}] Import count budget exceeded (${context.importCount + 1}/${context.resourceLimits.maxImportCount}) for "${normalized.id}".`,
    )
    return createResolvedImportSource()
  } else if (context.importedBytes + normalized.source.length > context.resourceLimits.maxImportedBytes) {
    // Drop nested import when total imported bytes exceed configured budget.
    pushImportWarning(
      context,
      'resource-limit',
      line,
      `[${sourceId}] Imported bytes budget exceeded (${context.importedBytes + normalized.source.length}/${context.resourceLimits.maxImportedBytes}) for "${normalized.id}".`,
    )
    return createResolvedImportSource()
  } else {
    // Continue nested import expansion inside configured budgets.
  }

  context.importCount += 1
  context.importedBytes += normalized.source.length
  context.importStack.push(normalized.id)
  const nested = resolveNested(normalized.source, normalized.id, context)
  context.importStack.pop()
  const result = createResolvedImportSource()
  appendWrappedImportSource(result, nested, sourceId, line)
  // Return inlined source at the exact import location.
  return result
}
